import { useQueries } from '@tanstack/react-query';
import { axiosGetV3 } from '../../api/axios-handler';
import useAuth from '../utils/useAuth';
import { parsePeersonaListenerRoleAllowlist, usePeersonaAccess } from '../utils/usePeersonaAccess';
import { PeerDetailLite } from './usePeers';
import { peersonaKeys, PeersonaChatBotMeta } from './usePeersonaChat';

/** Hydrated row for the Conversations / Start-New list views. */
export interface EligiblePeersona {
  listenerRoleId: number;
  chatbotId: number;
  name: string;
  avatarUrl: string | null;
  scopeLine: string | null;
  welcomeMessage: string | null;
}

async function fetchPeerLite(listenerRoleId: number): Promise<PeerDetailLite | null> {
  try {
    const res = await axiosGetV3(`/peers/${listenerRoleId}`, null);
    const data = res?.data;
    if (!data) return null;
    return { ...data, id: data.listener_role_id } as PeerDetailLite;
  } catch {
    // Peer hidden / deactivated / stale allowlist id — drop the row silently.
    return null;
  }
}

async function fetchPeersonaChatBot(chatbotId: number): Promise<PeersonaChatBotMeta | null> {
  try {
    const res = await axiosGetV3(`/peersona/${chatbotId}`, {});
    return (res?.data?.chatbot ?? null) as PeersonaChatBotMeta | null;
  } catch {
    return null;
  }
}

/**
 * Resolve the env-driven Peersona allowlist into rows for the list views.
 * Fans out: ``listenerRoleId`` → ``PeerDetailLite`` → ``chatbotId`` →
 * ``PeersonaChatBotMeta``. Shares cache keys with the per-id hooks so
 * the peer-details page's data is reused for free.
 */
export interface UseEligiblePeersonasResult {
  isLoading: boolean;
  peersonas: EligiblePeersona[];
}

export function useEligiblePeersonas(): UseEligiblePeersonasResult {
  const { featureEnabled } = usePeersonaAccess();
  // Defensive auth gate — see ``PeersonaProvider`` for context on why
  // anonymous fetches here would trip a 403 redirect loop.
  const { authToken } = useAuth();
  const isAuthenticated = Boolean(authToken);
  const allowedIds = Array.from(parsePeersonaListenerRoleAllowlist());

  const peerQueries = useQueries({
    queries: allowedIds.map((listenerRoleId) => ({
      // Same key shape as ``usePeerDetailLite`` so the cache is shared.
      queryKey: ['peerDetailLite', listenerRoleId],
      queryFn: () => fetchPeerLite(listenerRoleId),
      enabled: isAuthenticated && featureEnabled && allowedIds.length > 0,
      staleTime: 5 * 60_000,
    })),
  });

  const chatbotIdsByListener = allowedIds.map((listenerRoleId, idx) => {
    const peer = peerQueries[idx]?.data ?? null;
    return { listenerRoleId, peer, chatbotId: peer?.chatbot_id ?? null };
  });

  const chatbotMetaQueries = useQueries({
    queries: chatbotIdsByListener.map(({ chatbotId }) => ({
      queryKey: peersonaKeys.chatbot(chatbotId ?? -1),
      queryFn: () => (chatbotId != null ? fetchPeersonaChatBot(chatbotId) : Promise.resolve(null)),
      enabled: isAuthenticated && featureEnabled && chatbotId != null,
      staleTime: 5 * 60_000,
    })),
  });

  const isLoading =
    peerQueries.some((q) => q.isLoading || q.isFetching) || chatbotMetaQueries.some((q) => q.isLoading || q.isFetching);

  const peersonas: EligiblePeersona[] = chatbotIdsByListener
    .map(({ listenerRoleId, peer, chatbotId }, idx): EligiblePeersona | null => {
      if (!peer || chatbotId == null) return null;
      const chatbotMeta = chatbotMetaQueries[idx]?.data ?? null;
      const name = chatbotMeta?.display_name || chatbotMeta?.name || peer.user?.display_name || 'Peersona';
      // Read both the raw API field and the derived alias since either
      // ``PeerDetailLite`` or the heavier ``PeerDetail`` may be cached.
      const peerAny = peer as unknown as { profile_photo_url_square?: string; profile_photo_square_file_url?: string };
      const avatarUrl = peerAny.profile_photo_square_file_url || peerAny.profile_photo_url_square || null;
      return {
        listenerRoleId,
        chatbotId,
        name,
        avatarUrl,
        scopeLine: (chatbotMeta?.scope_line ?? '').trim() || null,
        welcomeMessage: (chatbotMeta?.welcome_message ?? '').trim() || null,
      };
    })
    .filter((row): row is EligiblePeersona => row !== null);

  return { isLoading, peersonas };
}
